import React, { createContext, useState, useEffect } from 'react';
import {
  ExposedContextData,
  Newsletter,
  GetNewslettersResponse,
  InterpolatedDataForUA,
} from '../types/types';
import { getNewslettersForUA, getNewslettersForSpecificEmail } from '../services/ua.api.service';
import { MOCKED_NEWSLETTERS } from '../constants/mocked_data';
import { subscribeToNewsletter } from '../../services/newsletter/newsletter.api.service';
import { GENERIC_ERROR_MESSAGE } from '../../components/react/alert_popup/constants';

interface Props extends InterpolatedDataForUA {
  children: React.ReactNode;
}

const UAContext = createContext<ExposedContextData>(null);

const UAContextProvider: React.FC<Props> = ({ children, ...props }: Props) => {
  const [emailFromUrl] = useState(props?.emailFromUrl);
  const [preselectedNewsletters, setPreselectedNewsletters] = useState<Newsletter[]>([]);
  const [isUAFormSubmitted, setIsUAFormSubmitted] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newsletters, setNewsletters] = useState<Newsletter[]>(MOCKED_NEWSLETTERS);
  const [deniedPrivileges, setDeniedPrivileges] = useState<string[]>([]);
  const [alertData, setAlertData] = useState(null);

  const resetState = (): void => {
    setIsUAFormSubmitted(false);
    setPreselectedNewsletters([]);
    setUserEmail('');
    setIsLoading(false);
    window.scrollTo(0, 0);
  };

  const onPostError = (error): void => {
    setAlertData({
      type: 'error',
      message: 'Your preferences couldn\'t be saved. ' + error?.message
    });
  };

  const onGetSuccess = (response: GetNewslettersResponse): void => {
    setNewsletters(
      response.data.filter((newsletter) =>
        ['general', 'focus_areas'].includes(newsletter.category)
      )
    );

    // UA user cannot subscribe / un-subscribe to newsletters which contain required privilege
    // There is one edge case for this rule: If Pro members, being logged out, directly type
    // `https://www.devex.com/my-newsletters?email=his@email.com`, they will see a 'Try Pro' link
    // in the Weekender block. The possibility of a Pro accessing this page as UA is extremely low.
    // As Account Holder, they should never receive links, in Marketo newsletters, that point to
    // `/my-newsletters`. In theory, as AHs, they only receive links  to `/account/newsletters`
    // We could at some point modify the code to ask for newsletter permissions when an email
    // is provided in the URL but again, as a AH, we make sure you never go to `/my-newsletters`
    setDeniedPrivileges(
      response.data
        .filter((newsletter) => newsletter?.required_privilege?.constant_name)
        .map((newsletter) => newsletter.required_privilege.constant_name)
    );
  };

  const onGetError = (): void => {
    setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, });
    setNewsletters([]);
  };

  const onGetFinally = (): void => {
    setIsLoading(false);
  };

  /* On component mount, fetch the list of newsletters and filter them by category:
   *   - 'general'
   *   - 'focus_areas'
   */
  useEffect((): void => {
    if (isLoading) return;
    setIsLoading(true);
    if (emailFromUrl) {
      getNewslettersForSpecificEmail(emailFromUrl)
        .then(onGetSuccess)
        .catch(onGetError)
        .finally(onGetFinally);
      return;
    }
    getNewslettersForUA()
      .then(onGetSuccess)
      .catch(onGetError)
      .finally(onGetFinally);

  }, [emailFromUrl]);

  useEffect((): void => {
    if (isLoading || !preselectedNewsletters.length || !isUAFormSubmitted) return;
    setIsLoading(true);
    const numberOfRequests = preselectedNewsletters.length;
    const firstItem = preselectedNewsletters[0];

    subscribeToNewsletter(
      firstItem,
      {
        email: userEmail,
        referrer: `${window.location.href}#${firstItem.permalink}`,
      }
    )
      .then((): Promise<void> => {
        if (numberOfRequests > 1) {
          const promises = preselectedNewsletters.slice(1).map((newsletter: Newsletter) =>
            subscribeToNewsletter(
              newsletter,
              {
                email: userEmail,
                referrer: `${window.location.href}#${newsletter.permalink}`,
              }
            )
          );

          return Promise.all(promises).catch(onPostError);
        }
      })
      .then(() => {
        setAlertData({
          type: 'success',
          message: `
          ${userEmail} was successfully subscribed to ${numberOfRequests}
           newsletter${numberOfRequests > 1 ? 's' : ''}
          `,
        });
      })
      .catch(onPostError)
      .finally(resetState);
  }, [isUAFormSubmitted]);

  const exposedContextData = {
    emailFromUrl,
    isLoading,
    newsletters,
    setNewsletters,
    deniedPrivileges,
    userEmail,
    setUserEmail,
    preselectedNewsletters,
    setPreselectedNewsletters,
    setIsUAFormSubmitted,
    alertData,
    setAlertData,
    // The following variables are only relevant for SignedInContext. Define and hardcode them.
    // The UI is resilient enough to manage this.
    isOptedOutOfAllEmails: false,
    isOptedOutOnFirstMount: false,
    toggleOptedOutOfAllEmails: (): null => null,
  };

  return <UAContext.Provider value={exposedContextData}>{children}</UAContext.Provider>;
};

export { UAContext, UAContextProvider };
